import React from 'react'
import { App, Button, Typography, notification as staticNotification } from 'antd'
import { useIntl } from 'react-intl'
import * as Sentry from '@sentry/react'

import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { SIGNIN_CANCELLED } from '../../../../util/openid/browser-runtime-v2'
import { trackSignin } from '../analytics'

const { Paragraph } = Typography

export const ADP = 'adp'

// Signing in is not connecting, and the sign-up page owns the CONNECT_WITH_* names, so this
// flow reports under its own. Walking away gets its own name rather than a property, because
// an abandoned attempt is a different thing from a broken one.
const PLATFORM_ERROR_EVENT: Record<string, string> = {
  [PlatformEnum.Microsoft]: 'SIGN_IN_WITH_MICROSOFT_ERROR',
  [PlatformEnum.Google]: 'SIGN_IN_WITH_GOOGLE_ERROR',
  [PlatformEnum.Slack]: 'SIGN_IN_WITH_SLACK_ERROR',
  [ADP]: 'SIGN_IN_WITH_ADP_ERROR',
}

const PLATFORM_CANCELED_EVENT: Record<string, string> = {
  [PlatformEnum.Microsoft]: 'SIGN_IN_WITH_MICROSOFT_CANCELED',
  [PlatformEnum.Google]: 'SIGN_IN_WITH_GOOGLE_CANCELED',
  [PlatformEnum.Slack]: 'SIGN_IN_WITH_SLACK_CANCELED',
  [ADP]: 'SIGN_IN_WITH_ADP_CANCELED',
}

const PLATFORM_LABEL: Record<string, string> = {
  [PlatformEnum.Microsoft]: 'Microsoft Teams',
  [PlatformEnum.Google]: 'Google',
  [PlatformEnum.Slack]: 'Slack',
  [ADP]: 'ADP',
}

// Dismissing the provider tab, declining consent and cancelling a Microsoft prompt all mean
// the same thing to the visitor, so they share one message.
const isCancellation = (errorMessage: string): boolean =>
  errorMessage.includes(SIGNIN_CANCELLED) || errorMessage.includes('access-denied-cancel') || errorMessage.includes('access_denied')

const readErrorMessage = (error): string => {
  if (typeof error === 'string') return error
  if (typeof error === 'object' && !(error instanceof Error)) {
    return error.error ? error.error : JSON.stringify(error, null, 2)
  }
  return error.toString()
}

export interface IUsePlatformErrorHandlerParams {
  onRetryMicrosoft: () => void
  /** Walking away is a page-level outcome, so the page says it rather than a corner toast. */
  onCancelled: (platform: string) => void
}

export interface IPlatformErrorHandler {
  handlePlatformError: (error, platform: string) => void
}

/**
 * Turns a failed platform sign-in into something the visitor can act on. Kept apart
 * from the sign-in flows because it is purely about explaining a failure.
 */
export const usePlatformErrorHandler = ({ onRetryMicrosoft, onCancelled }: IUsePlatformErrorHandlerParams): IPlatformErrorHandler => {
  const { formatMessage } = useIntl()
  const app = App.useApp()
  // Outside an <App> provider useApp() hands back empty placeholders, so fall back to
  // the static API rather than calling a method that is not there.
  const notification = typeof app?.notification?.error === 'function' ? app.notification : staticNotification

  const handlePlatformError = (error, platform: string) => {
    let errorMessage = readErrorMessage(error)
    const platformText = PLATFORM_LABEL[platform]
    // Read before the ADP branch below, which replaces the message both of these depend on.
    const cancelled = isCancellation(errorMessage)

    const eventName = cancelled ? PLATFORM_CANCELED_EVENT[platform] : PLATFORM_ERROR_EVENT[platform]
    if (eventName) {
      trackSignin(eventName, { errorMessage, platform })
    }

    if (cancelled) {
      onCancelled(platformText)
      return
    }

    if (platform === ADP) {
      errorMessage = formatMessage({ id: 'connect.platformErrorDescription' })
    }

    // Microsoft only: the copy names Microsoft and the button restarts a Microsoft sign-in.
    // Google emits `invalid_grant` too, and would otherwise land here: wrong words, wrong flow.
    const needsMicrosoftConsent = platform === PlatformEnum.Microsoft && (errorMessage.includes('interaction_required') || errorMessage.includes('invalid_grant'))

    if (needsMicrosoftConsent) {
      notification.error({
        message: formatMessage({ id: 'connect.interactionRequiredMSErrorTitle' }),
        description: (
          <div>
            <Paragraph>{formatMessage({ id: 'connect.interactionRequiredMSErrorDescription1' })}</Paragraph>
            <Paragraph>{formatMessage({ id: 'connect.interactionRequiredMSErrorDescription2' })}</Paragraph>
            <Paragraph keyboard copyable>
              {errorMessage}
            </Paragraph>
          </div>
        ),
        duration: 0,
        btn: <Button onClick={onRetryMicrosoft}>{formatMessage({ id: 'connect.login' })}</Button>,
      })
      return
    }

    notification.error({
      message: formatMessage({ id: 'connect.platformErrorTitle' }),
      description: (
        <div>
          <Paragraph>{formatMessage({ id: 'connect.platformErrorDescription1' })}</Paragraph>
          <Paragraph keyboard copyable>
            {errorMessage}
          </Paragraph>
          <Paragraph>{formatMessage({ id: 'connect.platformErrorDescription2' }, { platform: platformText })}</Paragraph>
        </div>
      ),
      duration: 0,
    })

    Sentry.captureException(error)
  }

  return { handlePlatformError }
}
